import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  MenuItem,
  Snackbar,
  Alert
} from "@mui/material";

const allRoles = [
  { label: "Administrador", value: "ADMIN" },
  { label: "Vendedor", value: "VENDOR" },
];

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  bgcolor: "#fff",
  borderRadius: "10px",
  p: 3,
  boxShadow: 24
};

const initialForm = {
  nombre: "",
  correo: "",
  telefono: "",
  rol: "VENDOR"
};

const NewUser = ({ open, onClose, onCreated, canCreateSuperUsers = false }) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    type: "success"
  });

  const roles = useMemo(() => {
    if (canCreateSuperUsers) return allRoles;
    return allRoles.filter((r) => r.value !== "SUPER");
  }, [canCreateSuperUsers]);

  useEffect(() => {
    if (open) {
      setForm({
        nombre: "",
        correo: "",
        telefono: "",
        rol: "VENDOR"
      });
    }
  }, [open]);

  useEffect(() => {
    if (!canCreateSuperUsers && form.rol === "SUPER") {
      setForm((prev) => ({ ...prev, rol: "VENDOR" }));
    }
  }, [canCreateSuperUsers, form.rol]);

  const getCSRF = async () => {
    const r = await fetch("/sistema/get-csrf-token/", { credentials: "include" });
    const d = await r.json();
    return d.csrfToken;
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const resetForm = () => {
    setForm({
      nombre: "",
      correo: "",
      telefono: "",
      rol: "VENDOR"
    });
  };

  const crearUsuario = async () => {
    if (!form.nombre.trim() || !form.correo.trim()) {
      setSnack({
        open: true,
        msg: "Nombre y correo son obligatorios",
        type: "error"
      });
      return;
    }

    if (!canCreateSuperUsers && form.rol === "SUPER") {
      setSnack({
        open: true,
        msg: "No tienes permisos para crear superusuarios",
        type: "error"
      });
      return;
    }

    try {
      setSaving(true);

      const csrf = await getCSRF();

      const payload = {
        nombre: form.nombre.trim(),
        correo: form.correo.trim(),
        telefono: form.telefono.trim(),
        rol: form.rol
      };

      const res = await fetch("/sistema/crear-usuario/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrf
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        resetForm();
        setSnack({
          open: true,
          msg: data?.msg || "Usuario creado correctamente",
          type: "success"
        });
        if (onCreated) await onCreated();
      } else {
        setSnack({
          open: true,
          msg: data?.error || data?.msg || "Error al crear usuario",
          type: "error"
        });
      }
    } catch {
      setSnack({
        open: true,
        msg: "Error de conexión",
        type: "error"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "#1565C0" }}
          >
            Crear nuevo usuario
          </Typography>

          <TextField
            label="Nombre completo"
            name="nombre"
            fullWidth
            value={form.nombre}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Correo electrónico"
            name="correo"
            fullWidth
            value={form.correo}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Teléfono"
            name="telefono"
            fullWidth
            value={form.telefono}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Rol"
            name="rol"
            select
            fullWidth
            value={form.rol}
            onChange={handleChange}
            sx={{ mb: 3 }}
          >
            {roles.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            fullWidth
            onClick={crearUsuario}
            disabled={saving}
            sx={{
              backgroundColor: "#1565C0",
              "&:hover": { backgroundColor: "#0D47A1" },
              textTransform: "none"
            }}
          >
            Guardar usuario
          </Button>
        </Box>
      </Modal>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.type} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewUser;